import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'components'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, Loader2 } from 'lucide-react'
import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import * as ErrorReporting from 'services/error-reporting/error-reporting'
import { useAnalytics } from 'hooks'
import { oauthUtils } from 'modules/oauth/utils/oauth-utils'
import { useLinkedInConnect } from 'modules/oauth/hooks/useLinkedInConnect'
import { IntegrationsService } from '../../../../openapi/services/IntegrationsService'
import { PlatformIcon } from 'components/PlatformIcon'
import { CONNECTABLE_CHANNEL_LABEL, type ConnectableChannel } from '../../utils/weekly-queue-utils'

export interface ModalPlatform {
  channel: ConnectableChannel
  postCount: number
  soonestScheduledAt?: string | null
}

// Per-post approval (and its `post_approval` trigger) is gone — the cockpit's
// only surviving connect-prompt entry point is the header CTA.
export type ConnectPromptTrigger = 'header_cta'

interface ConnectPlatformsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** One row per platform, sorted in display order. */
  platforms: ModalPlatform[]
  /** Business id required to scope the OAuth callback. */
  businessId: string
  /** Source of the prompt — drives analytics so we can compare effectiveness. */
  trigger: ConnectPromptTrigger
  /** Creative tied to the prompt, when one is in context. */
  creativeId?: string
  /** Called after each successful platform connection so the parent can refetch. */
  onConnected: (channel: ConnectableChannel) => void
}

const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
})
const TIME_FORMATTER = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: '2-digit',
})

function formatNext(iso?: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return `${DATE_FORMATTER.format(d)} · ${TIME_FORMATTER.format(d)}`
}

export function ConnectPlatformsModal({
  open,
  onOpenChange,
  platforms,
  businessId,
  trigger,
  creativeId,
  onConnected,
}: ConnectPlatformsModalProps) {
  const { track } = useAnalytics()
  const { resolveConnectProvider, chooser } = useLinkedInConnect()
  const [connectingChannel, setConnectingChannel] = useState<ConnectableChannel | null>(null)
  const [errorByChannel, setErrorByChannel] = useState<Partial<Record<ConnectableChannel, string>>>(
    {}
  )
  const [connectedInSession, setConnectedInSession] = useState<Set<string>>(new Set())
  const shownTrackedRef = useRef(false)
  const dismissedRef = useRef(false)

  // Snapshot the platforms list for the modal session: we don't want it to
  // shrink mid-session if the parent recomputes after each connect — that
  // would yank rows out from under the user as they're working.
  const sessionPlatforms = useMemo(() => platforms, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const allConnected = sessionPlatforms.every(p => connectedInSession.has(p.channel))

  useEffect(() => {
    if (!open) {
      shownTrackedRef.current = false
      dismissedRef.current = false
      setConnectingChannel(null)
      setErrorByChannel({})
      setConnectedInSession(new Set())
      return
    }
    if (shownTrackedRef.current) return
    shownTrackedRef.current = true
    track(AnalyticsEvents.Autopilot.WEEKLY_CONNECT_PROMPT_SHOWN, {
      trigger,
      creative_id: creativeId,
      channel: sessionPlatforms[0]?.channel,
      provider: sessionPlatforms[0]?.channel,
      platform: sessionPlatforms[0]?.channel,
      channels: sessionPlatforms.map(p => p.channel),
      posts_blocked: sessionPlatforms.reduce((sum, p) => sum + p.postCount, 0),
    })
  }, [open, track, trigger, creativeId, sessionPlatforms])

  // Auto-close once every platform in the session list is connected.
  useEffect(() => {
    if (!open || sessionPlatforms.length === 0) return
    if (allConnected) {
      dismissedRef.current = true
      onOpenChange(false)
    }
  }, [open, allConnected, sessionPlatforms.length, onOpenChange])

  const handleOpenChange = (next: boolean) => {
    if (!next && open && !dismissedRef.current) {
      dismissedRef.current = true
      const remaining = sessionPlatforms.map(p => p.channel).filter(c => !connectedInSession.has(c))
      track(AnalyticsEvents.Autopilot.WEEKLY_CONNECT_PROMPT_DISMISSED, {
        trigger,
        creative_id: creativeId,
        channel: sessionPlatforms[0]?.channel,
        provider: sessionPlatforms[0]?.channel,
        platform: sessionPlatforms[0]?.channel,
        channels_connected: Array.from(connectedInSession),
        channels_remaining: remaining,
      })
    }
    onOpenChange(next)
  }

  const handleConnect = async (channel: ConnectableChannel) => {
    // LinkedIn fronts two OAuth apps (personal `linkedin` / company `linkedin_org`).
    // Open the shared chooser first (when gated on); null = dismissed → abort before
    // the spinner. Other channels pass through. Both LinkedIn variants land on the
    // single `linkedin` row, so verification below stays keyed on `channel`.
    const oauthProvider = await resolveConnectProvider(channel)
    if (!oauthProvider) return
    setConnectingChannel(channel)
    setErrorByChannel(prev => {
      if (!prev[channel]) return prev
      const next = { ...prev }
      delete next[channel]
      return next
    })
    try {
      const result = await oauthUtils.openOAuthPopup(oauthProvider, {
        businessId,
        source: 'autopilot',
        track,
      })
      if (result === false) {
        setErrorByChannel(prev => ({
          ...prev,
          [channel]: 'Connection was cancelled or failed. Please try again.',
        }))
        return
      }
      // result === true | null — verify against the server before celebrating.
      const status = await IntegrationsService.integrationControllerGetIntegrationStatusV1(channel)
      if (status.isConnected) {
        setConnectedInSession(prev => {
          const next = new Set(prev)
          next.add(channel)
          return next
        })
        track(AnalyticsEvents.Autopilot.WEEKLY_CONNECT_PROMPT_CONNECTED, {
          trigger,
          creative_id: creativeId,
          channel,
          provider: channel,
          platform: channel,
        })
        onConnected(channel)
      } else {
        setErrorByChannel(prev => ({
          ...prev,
          [channel]: 'We couldn’t verify the connection. Please try again.',
        }))
      }
    } catch (error) {
      ErrorReporting.captureException(error, {
        tags: { feature: 'weekly_cockpit', step: 'connect_oauth', channel, trigger },
        extra: { businessId, creativeId },
      })
      console.error('ConnectPlatformsModal: OAuth flow failed', error)
      setErrorByChannel(prev => ({
        ...prev,
        [channel]: `Something went wrong connecting to ${CONNECTABLE_CHANNEL_LABEL[channel] ?? channel}.`,
      }))
    } finally {
      setConnectingChannel(null)
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent size="sm" variant="dark" withCloseButton>
          <DialogHeader>
            <DialogTitle variant="dark">Connect your accounts</DialogTitle>
            <DialogDescription variant="dark">
              Your scheduled posts can't publish until you connect:
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2">
            {sessionPlatforms.map(platform => {
              const isConnected = connectedInSession.has(platform.channel)
              const isConnecting = connectingChannel === platform.channel
              const isAnyConnecting = connectingChannel !== null
              const label = CONNECTABLE_CHANNEL_LABEL[platform.channel] ?? platform.channel
              const next = formatNext(platform.soonestScheduledAt)
              const error = errorByChannel[platform.channel]
              return (
                <div
                  key={platform.channel}
                  className="flex items-center gap-3 rounded-[12px] border border-line bg-bg-3 px-3 py-2.5"
                >
                  <PlatformIcon channel={platform.channel} size={40} className="rounded-[10px]" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-paper">{label}</p>
                    <p className="text-xs text-paper-dim">
                      {platform.postCount === 1
                        ? '1 post scheduled'
                        : `${platform.postCount} posts scheduled`}
                      {next ? ` · next ${next}` : ''}
                    </p>
                    {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
                  </div>
                  {isConnected ? (
                    <span className="flex shrink-0 items-center gap-1 text-sm font-medium text-acid">
                      <Check size={16} /> Connected
                    </span>
                  ) : (
                    <Button
                      variant="t3-primary"
                      size="t3-sm"
                      onClick={() => handleConnect(platform.channel)}
                      disabled={isAnyConnecting}
                    >
                      {isConnecting ? (
                        <>
                          <Loader2 size={14} className="animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        'Connect'
                      )}
                    </Button>
                  )}
                </div>
              )
            })}
          </div>
        </DialogContent>
      </Dialog>
      {chooser}
    </>
  )
}
